import { useState, useEffect, useCallback } from 'react';
import { documentConfigAPI } from '../services/documentConfigAPI';
import { toast } from 'react-hot-toast';

export const useConfiguration = (user) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [publicTemplates, setPublicTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  // Estados para modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);

  // Estados para formularios
  const [documentForm, setDocumentForm] = useState({
    nombre: '',
    descripcion: ''
  });

  const [fieldForm, setFieldForm] = useState({
    orden: '',
    nombre: '',
    especificacion: ''
  });

  // Estados para selecciones
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [newDocumentName, setNewDocumentName] = useState('');

  // Cargar tipos de documentos
  const loadDocumentTypes = useCallback(async () => {
    try {
      setLoading(true);
      if (user?.role === 'admin') {
        const response = await documentConfigAPI.getPublicTemplates();
        const items = (response.items || []).map(i => ({ ...i, campos: i.campos || [] }));
        setDocumentTypes(items);
      } else {
        const response = await documentConfigAPI.getMyDocumentTypes();
        const items = (response.items || []).map(i => ({ ...i, campos: i.campos || [] }));
        setDocumentTypes(items);
      }
    } catch (error) {
      toast.error(error.message || 'Error al cargar tipos de documentos');
      setDocumentTypes([]);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  // Cargar plantillas públicas
  const loadPublicTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = user?.role === 'admin' 
        ? await documentConfigAPI.getPublicTemplates()
        : await documentConfigAPI.getPublicTemplatesForUsers();
      const items = (response.items || []).map(i => ({ ...i, campos: i.campos || [] }));
      setPublicTemplates(items);
    } catch (error) {
      toast.error(error.message || 'Error al cargar plantillas públicas');
      setPublicTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Efecto para cargar datos iniciales
  useEffect(() => {
    if (user?.role) {
      loadDocumentTypes();
    }
  }, [user?.role, loadDocumentTypes]);

  // Efecto para mantener abierto el accordion
  useEffect(() => {
    if (openAccordion) {
      setTimeout(() => {
        const accordionElement = document.getElementById(`collapse${openAccordion}`);
        if (accordionElement && !accordionElement.classList.contains('show')) {
          new window.bootstrap.Collapse(accordionElement, {
            show: true
          });
        }
      }, 100);
    }
  }, [documentTypes, openAccordion]);

  return {
    // Estados
    documentTypes,
    publicTemplates,
    loading,
    loadingTemplates,
    openAccordion,
    setOpenAccordion,
    
    // Estados de modales
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    showTemplateModal,
    setShowTemplateModal,
    showFieldModal,
    setShowFieldModal,
    
    // Estados de formularios
    documentForm,
    setDocumentForm,
    fieldForm,
    setFieldForm,
    
    // Estados de selecciones
    selectedDocument,
    setSelectedDocument,
    selectedField,
    setSelectedField,
    selectedTemplate,
    setSelectedTemplate,
    newDocumentName,
    setNewDocumentName,
    
    // Funciones
    loadDocumentTypes,
    loadPublicTemplates
  };
};

export const useConfigurationActions = (user, configState) => {
  const {
    setDocumentForm,
    setFieldForm,
    setSelectedDocument,
    setSelectedField,
    setSelectedTemplate,
    setNewDocumentName,
    setShowAddModal,
    setShowEditModal,
    setShowTemplateModal,
    setShowFieldModal,
    setOpenAccordion,
    loadDocumentTypes,
    loadPublicTemplates
  } = configState;

  // Funciones para manejar documentos
  const handleAddDocument = async () => {
    if (!configState.documentForm.nombre.trim() || !configState.documentForm.descripcion.trim()) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    try {
      await documentConfigAPI.createDocumentType(configState.documentForm);
      setDocumentForm({ nombre: '', descripcion: '' });
      setShowAddModal(false);
      toast.success('Tipo de documento agregado exitosamente');
      await loadDocumentTypes();
    } catch (error) {
      toast.error(error.message || 'Error al agregar tipo de documento');
    }
  };

  const handleEditDocument = async () => {
    if (!configState.documentForm.nombre.trim() || !configState.documentForm.descripcion.trim()) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    try {
      await documentConfigAPI.updateDocumentType(configState.selectedDocument.id, configState.documentForm);
      setDocumentForm({ nombre: '', descripcion: '' });
      setSelectedDocument(null);
      setShowEditModal(false);
      toast.success('Tipo de documento actualizado exitosamente');
      await loadDocumentTypes();
    } catch (error) {
      toast.error(error.message || 'Error al actualizar tipo de documento');
    }
  };

  const handleDeleteDocument = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este tipo de documento? Se perderán todos sus campos.')) {
      try {
        await documentConfigAPI.deleteDocumentType(id);
        toast.success('Tipo de documento eliminado');
        await loadDocumentTypes();
      } catch (error) {
        toast.error(error.message || 'Error al eliminar tipo de documento');
      }
    }
  };

  // Funciones para manejar campos
  const handleAddField = async () => {
    if (!configState.fieldForm.orden || !configState.fieldForm.nombre.trim()) {
      toast.error('Por favor complete los campos obligatorios (Orden y Nombre)');
      return;
    }

    const documentId = configState.selectedDocument.id;
    try {
      // Construir el objeto de datos solo con campos que tienen valor
      const fieldData = {
        orden: parseInt(configState.fieldForm.orden),
        nombre: configState.fieldForm.nombre.trim()
      };
      
      // Solo incluir especificación si tiene contenido
      if (configState.fieldForm.especificacion && configState.fieldForm.especificacion.trim()) {
        fieldData.especificacion = configState.fieldForm.especificacion.trim();
      } else {
        fieldData.especificacion = '';
      }

      await documentConfigAPI.createFieldByRole(configState.selectedDocument.id, fieldData, user?.role);
      setFieldForm({ orden: '', nombre: '', especificacion: '' });
      setShowFieldModal(false);
      toast.success('Campo agregado exitosamente');
      await loadDocumentTypes();
      setOpenAccordion(documentId);
    } catch (error) {
      toast.error(error.message || 'Error al agregar campo');
    }
  };

  const handleEditField = async () => {
    if (!configState.fieldForm.orden || !configState.fieldForm.nombre.trim()) {
      toast.error('Por favor complete los campos obligatorios (Orden y Nombre)');
      return;
    }

    const documentId = configState.selectedDocument.id;
    try {
      // Construir el objeto de datos solo con campos que tienen valor
      const fieldData = {
        orden: parseInt(configState.fieldForm.orden),
        nombre: configState.fieldForm.nombre.trim()
      };
      
      // Solo incluir especificación si tiene contenido
      if (configState.fieldForm.especificacion && configState.fieldForm.especificacion.trim()) {
        fieldData.especificacion = configState.fieldForm.especificacion.trim();
      } else {
        fieldData.especificacion = '';
      }

      await documentConfigAPI.updateFieldByRole(configState.selectedField.id, fieldData, user?.role);
      setFieldForm({ orden: '', nombre: '', especificacion: '' });
      setSelectedField(null);
      setShowFieldModal(false);
      toast.success('Campo actualizado exitosamente');
      await loadDocumentTypes();
      setOpenAccordion(documentId);
    } catch (error) {
      toast.error(error.message || 'Error al actualizar campo');
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (window.confirm('¿Está seguro de eliminar este campo?')) {
      const documentWithField = configState.documentTypes.find(doc => 
        doc.campos.some(campo => campo.id === fieldId)
      );
      const documentId = documentWithField?.id;
      
      try {
        await documentConfigAPI.deleteFieldByRole(fieldId, user?.role);
        toast.success('Campo eliminado');
        await loadDocumentTypes();
        if (documentId) {
          setOpenAccordion(documentId);
        }
      } catch (error) {
        toast.error(error.message || 'Error al eliminar campo');
      }
    }
  };

  // Nueva función para actualizar campo inline
  const handleUpdateFieldInline = async (fieldId, fieldData) => {
    if (!fieldData.nombre.trim()) {
      throw new Error('El nombre del campo es obligatorio');
    }

    try {
      // Construir el objeto de datos
      const updateData = {
        orden: parseInt(fieldData.orden) || 1,
        nombre: fieldData.nombre.trim(),
        especificacion: fieldData.especificacion?.trim() || ''
      };

      await documentConfigAPI.updateFieldByRole(fieldId, updateData, user?.role);
      await loadDocumentTypes();
      toast.success('Campo actualizado exitosamente');
    } catch (error) {
      toast.error(error.message || 'Error al actualizar campo');
      throw error; // Re-lanzar para manejar en el componente
    }
  };

  // Funciones de plantillas
  const handleInitializeDefaultData = async () => {
    if (window.confirm('¿Desea inicializar los tipos de documentos por defecto? Esto no afectará los datos existentes.')) {
      try {
        const response = await documentConfigAPI.initializeDefaultData();
        toast.success(response.message);
        await loadDocumentTypes();
      } catch (error) {
        toast.error(error.message || 'Error al inicializar datos por defecto');
      }
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!configState.selectedTemplate) {
      toast.error('Por favor seleccione una plantilla');
      return;
    }

    if (!configState.newDocumentName.trim()) {
      toast.error('Por favor ingrese un nombre para el documento');
      return;
    }

    try {
      await documentConfigAPI.createFromTemplate(configState.selectedTemplate, configState.newDocumentName);
      setSelectedTemplate('');
      setNewDocumentName('');
      setShowTemplateModal(false);
      toast.success('Documento creado exitosamente desde plantilla');
      await loadDocumentTypes();
    } catch (error) {
      toast.error(error.message || 'Error al crear documento desde plantilla');
    }
  };

  // Funciones para abrir modales
  const openAddModal = () => {
    setDocumentForm({ nombre: '', descripcion: '' });
    setShowAddModal(true);
  };

  const openEditModal = (document) => {
    setSelectedDocument(document);
    setDocumentForm({ nombre: document.nombre, descripcion: document.descripcion });
    setShowEditModal(true);
  };

  const openTemplateModal = async () => {
    setShowTemplateModal(true);
    await loadPublicTemplates();
  };

  const openFieldModal = (document, field = null) => {
    setSelectedDocument(document);
    setOpenAccordion(document.id);
    if (field) {
      setSelectedField(field);
      setFieldForm({ 
        orden: field.orden.toString(), 
        nombre: field.nombre, 
        especificacion: field.especificacion 
      });
    } else {
      setSelectedField(null);
      const nextOrder = document.campos.length > 0 
        ? Math.max(...document.campos.map(c => c.orden)) + 1 
        : 1;
      setFieldForm({ orden: nextOrder.toString(), nombre: '', especificacion: '' });
    }
    setShowFieldModal(true);
  };

  return {
    handleAddDocument,
    handleEditDocument,
    handleDeleteDocument,
    handleAddField,
    handleEditField,
    handleDeleteField,
    handleUpdateFieldInline,
    handleInitializeDefaultData,
    handleCreateFromTemplate,
    openAddModal,
    openEditModal,
    openTemplateModal,
    openFieldModal
  };
};
